import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import axios from 'axios';
import { AlertTriangle, CheckCircle, Trash2, RefreshCw } from 'lucide-react';

const AdminViagemPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [carregando, setCarregando] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [viagensTravadas, setViagensTravadas] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [resultado, setResultado] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const carregarViagensTravadas = useCallback(async () => {
    setCarregando(true);
    try {
      const token = localStorage.getItem('token');
      const [emAndamento, pausadas, sincronizando] = await Promise.all([
        axios.get(`${API_URL}/api/viagens?status=em_andamento`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/viagens?status=pausada`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        // "sincronizando" fica presa quando o app mobile não consegue confirmar
        // o encerramento com o servidor (rede caiu, app fechado, etc.)
        axios.get(`${API_URL}/api/viagens?status=sincronizando`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const todas = [...emAndamento.data, ...pausadas.data, ...sincronizando.data].sort(
        (a, b) => new Date(a.data_hora_inicio) - new Date(b.data_hora_inicio)
      );

      setViagensTravadas(todas);
      setSelecionadas([]);
    } catch (error) {
      console.error('Erro ao carregar viagens travadas:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar viagens travadas",
        variant: "destructive"
      });
    } finally {
      setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  useEffect(() => {
    carregarViagensTravadas();
  }, [carregarViagensTravadas]);

  const toggleSelecionada = (viagemId) => {
    setSelecionadas((prev) =>
      prev.includes(viagemId) ? prev.filter((id) => id !== viagemId) : [...prev, viagemId]
    );
  };

  const toggleTodas = () => {
    setSelecionadas((prev) =>
      prev.length === viagensTravadas.length ? [] : viagensTravadas.map((v) => v.id)
    );
  };

  const encerrarSelecionadas = async () => {
    if (selecionadas.length === 0) return;
    if (!window.confirm(`Tem certeza que deseja FORÇAR o encerramento de ${selecionadas.length} viagem(ns)?`)) {
      return;
    }

    setProcessando(true);
    setResultado(null);

    try {
      const token = localStorage.getItem('token');
      const respostas = await Promise.all(
        selecionadas.map((viagemId) =>
          axios.post(
            `${API_URL}/api/viagens/${viagemId}/forcar-encerramento`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          ).then((res) => ({ ok: true, ...res.data }))
            .catch((error) => ({ ok: false, id: viagemId, erro: error.response?.data?.detail || 'Erro desconhecido' }))
        )
      );

      const sucesso = respostas.filter((r) => r.ok);
      const falhas = respostas.filter((r) => !r.ok);

      setResultado({ sucesso, falhas });

      toast({
        title: falhas.length === 0 ? "Sucesso" : "Concluído com falhas",
        description: `${sucesso.length} viagem(ns) encerrada(s)${falhas.length > 0 ? `, ${falhas.length} falha(s)` : ''}`,
        variant: falhas.length === 0 ? "default" : "destructive"
      });

      carregarViagensTravadas();
    } catch (error) {
      console.error('Erro ao encerrar viagens:', error);
      toast({
        title: "Erro",
        description: "Erro ao encerrar viagens selecionadas",
        variant: "destructive"
      });
    } finally {
      setProcessando(false);
    }
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    return new Date(dataISO).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">🔧 Admin - Viagens</h1>
            <p className="text-gray-600 mt-2">Ferramentas de emergência para resolver viagens travadas</p>
          </div>
          <Button
            onClick={() => navigate('/historico-viagens')}
            variant="outline"
            className="self-start shrink-0"
          >
            📍 Histórico de Viagens
          </Button>
        </div>

        {/* Aviso */}
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div className="text-sm">
                <p className="font-semibold text-yellow-900 mb-2">⚠️ Atenção!</p>
                <p className="text-yellow-800">
                  Escolha abaixo quais viagens travadas (status &quot;em_andamento&quot; ou &quot;pausada&quot;) deseja encerrar
                  à força. Isso marca a viagem como &quot;finalizada&quot; e permite iniciar uma nova.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lista de viagens travadas */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center space-x-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              <span>Viagens Travadas ({viagensTravadas.length})</span>
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={carregarViagensTravadas} disabled={carregando}>
              <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {carregando ? (
              <p className="text-sm text-gray-500">Carregando...</p>
            ) : viagensTravadas.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhuma viagem travada no momento. 🎉</p>
            ) : (
              <>
                <label className="flex items-center space-x-2 text-sm text-gray-600 pb-2 border-b">
                  <input
                    type="checkbox"
                    checked={selecionadas.length === viagensTravadas.length}
                    onChange={toggleTodas}
                  />
                  <span>Selecionar todas</span>
                </label>

                <div className="space-y-2">
                  {viagensTravadas.map((viagem) => (
                    <div
                      key={viagem.id}
                      className="flex items-center justify-between p-3 rounded border border-gray-200 bg-white"
                    >
                      <label className="flex items-center space-x-3 flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selecionadas.includes(viagem.id)}
                          onChange={() => toggleSelecionada(viagem.id)}
                        />
                        <div className="text-sm">
                          <p className="font-semibold text-gray-900">{viagem.funcionario_nome || 'Desconhecido'}</p>
                          <p className="text-gray-500">
                            Início: {formatarData(viagem.data_hora_inicio)} · Status: {viagem.status} · {viagem.km_rodados || 0} km
                          </p>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={encerrarSelecionadas}
                  disabled={processando || selecionadas.length === 0}
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  {processando
                    ? 'Processando...'
                    : `🔧 Forçar Encerramento (${selecionadas.length} selecionada${selecionadas.length === 1 ? '' : 's'})`}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Resultado */}
        {resultado && (
          <Card className="border-green-300 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-green-900 mb-2">
                    ✅ {resultado.sucesso.length} viagem(ns) encerrada(s) com sucesso
                  </p>

                  {resultado.sucesso.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {resultado.sucesso.map((v) => (
                        <div key={v.id} className="bg-white p-3 rounded border border-green-200 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-gray-600">ID:</span>
                              <p className="font-mono text-xs">{v.id.substring(0, 8)}...</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Status Anterior:</span>
                              <p className="font-semibold">{v.status_anterior}</p>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-600">KM Rodados:</span>
                              <p className="font-semibold">{v.km_rodados} km</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {resultado.falhas.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-semibold text-red-800">Falhas:</p>
                      {resultado.falhas.map((f) => (
                        <div key={f.id} className="bg-white p-3 rounded border border-red-200 text-sm text-red-700">
                          {f.id.substring(0, 8)}...: {f.erro}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Voltar */}
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="w-full"
        >
          ← Voltar ao Menu
        </Button>

      </div>
    </div>
  );
};

export default AdminViagemPage;
